import { Link } from "@tanstack/react-router";
import { isRTLLanguage } from "@/utils/helpers";
import { AppShell } from "@/components/Navbar";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useListCirclesQuery } from "@/api/circles-api";
import { useGetAuthenticatedUser } from "@/api/user";
import { Circle } from "@/types/circle";
import JoinEfb from "./sections/JoinEfb";
import efbContainerImage from "@/assets/images/efb-container-image.jpg";
import ImpactToDate from "./sections/ImpactToDate";
import LandingCards from "./sections/LandingCards";
import { useTour } from "@/hooks/useTour";
import { useEffect } from "react";
import { useGetLandingPageContent } from "@/api/landing-page-content";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollIndicator } from "@/components/Helpers/ScrollIndicator";

const CohLanding = () => {
  const { t, i18n } = useTranslation();
  const { data: circles, isLoading: circlesLoading } = useListCirclesQuery({
    is_owned_by_efb_directly: true,
  });
  const { data: user } = useGetAuthenticatedUser();
  const { data: userOwnedCircles, isLoading: userCirclesLoading } = useListCirclesQuery({
    is_joined: true,
  });
  const { startTour, tourState, getTourStatus, restoreTourIfIncomplete } = useTour();

  // Auto-start tour for logged-in users who haven't created any circles
  useEffect(() => {
    // Only proceed if user is authenticated and no tour is active
    if (
      !user ||
      tourState.isActive ||
      circlesLoading ||
      !circles?.data?.results?.length ||
      !userOwnedCircles?.data.results ||
      userCirclesLoading
    ) {
      return;
    }

    let timeoutId: NodeJS.Timeout | null = null;

    // PRIORITY 1: Handle circles-page tour logic first (for users who already joined EFB circle)
    const firstCircle = circles.data.results[0];
    if (firstCircle.is_user_joined) {
      const circlesPageTourStatus = getTourStatus("circles-page");

      // If incomplete, restore it
      if (circlesPageTourStatus === "incomplete") {
        restoreTourIfIncomplete("circles-page");
        return;
      }

      if (circlesPageTourStatus === "not-started") {
        timeoutId = setTimeout(() => {
          startTour("circles-page", 4);
        }, 500);
        return;
      }
    }

    // PRIORITY 2: Handle create-circle tour logic (only if circles-page tour doesn't apply)
    const createCircleTourStatus = getTourStatus("create-circle");

    // If incomplete, restore it
    if (createCircleTourStatus === "incomplete") {
      restoreTourIfIncomplete("create-circle");
      return;
    }

    if (createCircleTourStatus === "not-started") {
      // Check if user has any circles they own (not just joined)
      const hasOwnedCircles = userOwnedCircles.data.results.some(
        (circle: Circle) => circle.owner?.id === user.id,
      );
      if (!hasOwnedCircles) {
        // User is logged in but hasn't created any circles - start the tour
        timeoutId = setTimeout(() => {
          startTour("create-circle");
        }, 500);
        return;
      }
    }

    // Cleanup function to clear timeout if component unmounts or dependencies change
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    user?.id, // Only depend on user ID, not the whole user object
    userCirclesLoading,
    circlesLoading,
    tourState.isActive,
    userOwnedCircles?.data?.count, // Use count instead of the results array
    circles?.data?.count, // Use count instead of the results array
  ]);

  const {
    data: landingPageContent,
    isLoading: landingPageLoading,
    error: landingPageError,
  } = useGetLandingPageContent(i18n.language);

  return (
    <main>
      <div className="flex h-14 items-center justify-between bg-foreground px-4 md:px-[24px]">
        <div className="flex flex-row gap-1 text-xs text-white md:text-sm">
          {t("poweredBy.first_part")}{" "}
          <Link aria-label={t("poweredBy.first_part") + " " + t("poweredBy.second_part")} to="/">
            <p className="font-bold text-white underline"> {t("poweredBy.second_part")}</p>
          </Link>
        </div>
        <Link aria-label={t("join.first_part") + " " + t("join.second_part")} to="/coh">
          {isRTLLanguage(i18n.language) ? (
            <img src="/coh-logo-ar-white.svg" className="h-8" />
          ) : (
            <img src="/coh-logo-white.svg" className="h-8" />
          )}
        </Link>
      </div>

      <AppShell theme="dark" navbarClass="top-14 absolute w-full" light logo="coh" />

      <div
        className="dark relative h-[682px] w-full bg-cover bg-center object-cover"
        style={{
          backgroundImage: "url('/landing-hero.jpg')",
        }}
      >
        <div className="flex h-full flex-shrink-0 flex-col items-start justify-end self-stretch p-4 sm:flex-row sm:items-end sm:justify-start sm:p-6 sm:pb-0">
          <div className="mb-6 w-full">
            <h1 className="text-[40px] font-650 leading-[100%] text-[#EDEAE4] md:text-8xl ">
              {landingPageLoading ? (
                <Skeleton className="h-48 w-full" />
              ) : landingPageError ? (
                t("landingPage.title") // Fallback to translation
              ) : (
                landingPageContent?.title || t("landingPage.title")
              )}
            </h1>
          </div>
          <ScrollIndicator href="#description" mode="light" />
        </div>
      </div>

      <section className="mx-4 my-8 md:mx-auto md:my-16 md:px-20 lg:px-80" id="description">
        <p className="text-xl font-350 leading-[120%] md:text-2xl md:leading-8">
          {landingPageLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : landingPageError ? (
            t("landingPage.description") // Fallback to translation
          ) : (
            landingPageContent?.description || t("landingPage.description")
          )}
        </p>
      </section>
      <LandingCards
        isContentLoading={landingPageLoading}
        content={landingPageContent?.main_cards}
      />
      <ImpactToDate
        isContentLoading={landingPageLoading}
        content={landingPageContent?.our_impact_to_date}
      />
      <div className="flex flex-col gap-6 px-6 md:min-h-[580px] md:flex-row">
        <div className="relative flex min-h-[468px] flex-1 flex-col justify-end rounded-2xl bg-[#E2DDD7] p-6 md:min-h-[580px] ">
          <img
            src={efbContainerImage}
            alt="efb container"
            className="absolute end-6 top-6 h-28 w-52 rounded-2xl md:h-36 md:w-64"
          />
          <h3 className="mb-4 text-2xl font-650 leading-[120%] md:mb-6 md:text-5xl">
            {landingPageLoading ? (
              <Skeleton className="h-12 w-48" />
            ) : (
              landingPageContent?.about_us.title || t("landingPage.efb")
            )}
          </h3>
          <p className="mb-4 text-base font-350 leading-[140%] md:mb-8 md:text-2xl md:leading-8">
            {landingPageLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : (
              landingPageContent?.about_us.description || t("landingPage.efbDescription")
            )}
          </p>
          <Button size="lg" className="md:w-fit" asChild>
            <Link to="/donate-now" search={{ tab: "", source: "coh" }}>
              {t("landingPage.donateNow")}
            </Link>
          </Button>
        </div>
        {circles?.data.results.map((circle) => <JoinEfb key={circle.id} circle={circle} />)}
      </div>
    </main>
  );
};

export default CohLanding;
